"use client";

import * as React from "react";
import { Callsign } from "@modernatc/ui/callsign";
import {
  calculate,
  formatHours,
  formatUsd,
  type TierChoice,
} from "@/lib/roi";

const TIER_LABELS: Record<TierChoice, string> = {
  trainee_yearly: "Trainee yearly ($89)",
  pro_yearly: "Pro yearly ($249)",
};

/**
 * Client-side ROI calculator surfaced on /pricing.
 *
 * Computes three numbers users actually weigh:
 *   - Total subscription cost across the study window
 *   - Effective $/hour
 *   - Expected-value multiple if our method nudges washout risk down
 *     by even a single percentage point
 *
 * Sliders are debounced via React's normal re-render path — the math
 * is microseconds and the output is just text. No animation, no
 * tooltips; the page is dense already.
 */
export function RoiCalculator() {
  const [months, setMonths] = React.useState(6);
  const [dailyMin, setDailyMin] = React.useState(25);
  const [tier, setTier] = React.useState<TierChoice>("pro_yearly");

  const result = React.useMemo(
    () =>
      calculate({
        monthsUntilTest: months,
        dailyStudyMinutes: dailyMin,
        tier,
      }),
    [months, dailyMin, tier],
  );

  return (
    <section className="border-b border-[var(--color-panel-edge)] py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Callsign className="text-xs text-[var(--color-amber)]">
          § 03 · ROI
        </Callsign>
        <h2 className="mt-4 max-w-3xl text-balance text-3xl font-semibold tracking-tight text-[var(--color-ink)] sm:text-4xl">
          What does this actually cost per hour studied?
        </h2>
        <p className="mt-4 max-w-2xl text-pretty text-[var(--color-ink-dim)]">
          Dial in your study window and daily minutes. The numbers
          update as you move the sliders. The expected-value figure
          assumes our method reduces personal washout risk. Your
          success rate could be higher or lower.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          {/* Controls */}
          <div className="space-y-6 rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)]/40 p-6">
            <Field
              label="Months until Class Convening Date"
              value={`${months} mo`}
            >
              <input
                type="range"
                min={1}
                max={18}
                step={1}
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                aria-label="Months until your test or Class Convening Date"
                className="w-full accent-[var(--color-amber)]"
              />
            </Field>

            <Field label="Average daily study" value={`${dailyMin} min`}>
              <input
                type="range"
                min={5}
                max={120}
                step={5}
                value={dailyMin}
                onChange={(e) => setDailyMin(Number(e.target.value))}
                aria-label="Average daily study minutes"
                className="w-full accent-[var(--color-amber)]"
              />
            </Field>

            <Field label="Tier">
              <div className="grid gap-2">
                {(Object.keys(TIER_LABELS) as TierChoice[]).map((t) => (
                  <label
                    key={t}
                    className={`flex cursor-pointer items-center gap-3 rounded-[var(--radius-sm)] border px-3 py-2 text-sm transition ${
                      tier === t
                        ? "border-[var(--color-amber)] bg-[var(--color-panel)] text-[var(--color-ink)]"
                        : "border-[var(--color-panel-edge)] text-[var(--color-ink-dim)] hover:border-[var(--color-amber)]/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value={t}
                      checked={tier === t}
                      onChange={() => setTier(t)}
                      className="accent-[var(--color-amber)]"
                    />
                    <span>{TIER_LABELS[t]}</span>
                  </label>
                ))}
              </div>
            </Field>
          </div>

          {/* Output */}
          <dl className="grid gap-4 sm:grid-cols-2">
            <Stat
              label="Total subscription cost"
              value={formatUsd(result.totalCost)}
              hint={`across ${months} month${months === 1 ? "" : "s"}`}
            />
            <Stat
              label="Effective hourly cost"
              value={formatUsd(result.hourlyCost)}
              hint={`based on ${formatHours(result.totalHours)} studied`}
              highlight
            />
            <Stat
              label="Expected first-year value"
              value={formatUsd(result.expectedSalaryValue)}
              hint="if method cuts washout risk"
            />
            <Stat
              label="Value ÷ cost"
              value={`${result.evMultiple.toFixed(0)}×`}
              hint="of subscription outlay"
              highlight
            />
          </dl>
        </div>

        <p className="mt-8 max-w-3xl text-xs text-[var(--color-ink-muted)]">
          Expected-value math uses an FAA controller (CPC) first-year
          salary of $128,000 (round number; actual depends on facility
          and locality). Washout-risk reduction is a personal estimate;
          we surface it as 1 point by default to keep the framing
          conservative. Subscription costs follow the published pricing
          above.
        </p>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
          {label}
        </span>
        {value ? (
          <span className="text-callsign text-[10px] text-[var(--color-amber)]">
            {value}
          </span>
        ) : null}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-[var(--radius-md)] border p-5 ${
        highlight
          ? "border-[var(--color-amber)]/40 bg-[var(--color-panel)]"
          : "border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)]/30"
      }`}
    >
      <dt className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
        {label}
      </dt>
      <dd
        className={`mt-2 text-3xl font-semibold tracking-tight ${
          highlight
            ? "text-[var(--color-amber-bright)]"
            : "text-[var(--color-ink)]"
        }`}
      >
        {value}
      </dd>
      {hint ? (
        <p className="mt-2 text-xs text-[var(--color-ink-muted)]">{hint}</p>
      ) : null}
    </div>
  );
}
